/**
 * FILE: packages/supabase/src/services/hubCollaboration.ts
 * PURPOSE: Supabase service layer — database access and business logic.
 * @deprecated Use domain-specific subpaths instead. This file will be removed in Phase 4.
 * AGENT_CONTEXT: All DB access goes through service layer. Call logActivity() on writes.
 * INVARIANTS: Soft-delete filter, phone PK protection, activity logging.
 * LAST_REVIEWED: 2026-05-07
 */


import { logActivity, withActivityActor, type ActivityActor } from '../internal/activity'
import { ServiceError } from '../internal/errors'
import type { DbClient } from "../dbClient";
import { defaultDbClient } from "../dbClient";
import type { Json, TablesUpdate } from '../database.gen'

export interface HubThread {
  id: string
  title: string
  entity_type: string | null
  entity_id: string | null
  status: 'open' | 'resolved'
  created_by: string
  created_at: string
  updated_at: string
}

export interface HubThreadMessage {
  id: string
  thread_id: string
  sender_id: string
  sender_name: string
  sender_role: string
  content: string
  mentions: Array<{ type: string; id: string; label: string }>
  created_at: string
  edited_at: string | null
}

export interface HubMention {
  type: 'staff' | 'student' | 'job' | 'application'
  id: string
  label: string
}

export interface CreateThreadPayload {
  title: string
  entity_type?: string | null
  entity_id?: string | null
}

export interface SendMessagePayload {
  thread_id: string
  content: string
  mentions?: HubMention[]
}

export async function getThreads(opts?: {
      status?: string
      entityType?: string
      entityId?: string
      limit?: number
    }, client: DbClient = defaultDbClient): Promise<(HubThread & { unread_count: number; message_count: number })[]> {
  let q = client
    .from('hub_threads')
    .select(
      '*, messages:hub_thread_messages(count), participants:hub_thread_participants(unread_count)',
      { count: 'exact' }
    )
    .order('updated_at', { ascending: false })
    .limit(opts?.limit ?? 50)

  if (opts?.status) q = q.eq('status', opts.status)
  if (opts?.entityType) q = q.eq('entity_type', opts.entityType)
  if (opts?.entityId) q = q.eq('entity_id', opts.entityId)

  const { data, error } = await q
  if (error) throw new ServiceError(error.message, 'HUB_THREADS_LIST', error)

  return (data ?? []).map((row) => {
    const r = row as Record<string, unknown>
    const messages = (r.messages ?? []) as Array<{ count?: number }>
    const participants = (r.participants ?? []) as Array<{ unread_count?: number }>
    return {
      ...r,
      message_count: messages[0]?.count ?? 0,
      unread_count: participants.reduce((a, p) => a + (p.unread_count ?? 0), 0),
    }
  }) as (HubThread & { unread_count: number; message_count: number })[]
}

export async function getThreadMessages(
  threadId: string,
  opts?: { limit?: number; offset?: number }, client: DbClient = defaultDbClient
): Promise<HubThreadMessage[]> {
  const { data, error } = await client
    .from('hub_thread_messages')
    .select('*')
    .eq('thread_id', threadId)
    .order('created_at', { ascending: true })
    .limit(opts?.limit ?? 100)
    .range(opts?.offset ?? 0, (opts?.offset ?? 0) + (opts?.limit ?? 100) - 1)

  if (error) throw new ServiceError(error.message, 'HUB_MESSAGES_LIST', error)
  return (data ?? []) as HubThreadMessage[]
}

export async function createThread(
  payload: CreateThreadPayload,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<HubThread> {
  const { data, error } = await client
    .from('hub_threads')
    .insert({
      title: payload.title,
      entity_type: payload.entity_type ?? null,
      entity_id: payload.entity_id ?? null,
      created_by: actor.userId,
    })
    .select()
    .single()

  if (error) throw new ServiceError(error.message, 'HUB_THREAD_CREATE', error)
  if (!data) throw new ServiceError('No row returned', 'HUB_THREAD_CREATE')

  // Auto-join creator as participant
  await client.from('hub_thread_participants').insert({
    thread_id: data.id,
    staff_id: actor.userId,
    last_read_at: new Date().toISOString(),
    unread_count: 0,
  })

  await logActivity(
    withActivityActor(actor, {
      action: 'HUB_THREAD_CREATE',
      entityType: 'hub_threads',
      entityId: data.id,
      newValues: payload as unknown as Record<string, unknown>,
    }), client
  )

  return data as HubThread
}

export async function sendThreadMessage(
  payload: SendMessagePayload,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<HubThreadMessage> {
  const { data, error } = await client
    .from('hub_thread_messages')
    .insert({
      thread_id: payload.thread_id,
      sender_id: actor.userId,
      sender_name: actor.userName,
      sender_role: actor.userRole,
      content: payload.content,
      mentions: (payload.mentions ?? []) as unknown as Json,
    })
    .select()
    .single()

  if (error) throw new ServiceError(error.message, 'HUB_MESSAGE_SEND', error)
  if (!data) throw new ServiceError('No row returned', 'HUB_MESSAGE_SEND')

  // Increment unread for all participants except sender
  await client.rpc('increment_thread_unread', { p_thread_id: payload.thread_id, p_sender_id: actor.userId })

  // Create mention notifications
  const mentions = payload.mentions ?? []
  for (const m of mentions) {
    if (m.type === 'staff') {
      await client.from('hub_mention_notifications').insert({
        message_id: data.id,
        mentioned_staff_id: m.id,
        thread_id: payload.thread_id,
      })
    }
  }

  return data as HubThreadMessage
}

export async function markThreadRead(threadId: string, staffId: string, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client
    .from('hub_thread_participants')
    .upsert(
      {
        thread_id: threadId,
        staff_id: staffId,
        last_read_at: new Date().toISOString(),
        unread_count: 0,
      },
      { onConflict: 'thread_id,staff_id' }
    )

  if (error) throw new ServiceError(error.message, 'HUB_THREAD_READ', error)
}

export async function resolveThread(threadId: string, actor: ActivityActor, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client
    .from('hub_threads')
    .update({ status: 'resolved' })
    .eq('id', threadId)

  if (error) throw new ServiceError(error.message, 'HUB_THREAD_RESOLVE', error)

  await logActivity(
    withActivityActor(actor, {
      action: 'HUB_THREAD_RESOLVE',
      entityType: 'hub_threads',
      entityId: threadId,
    }), client
  )
}

export interface HubMentionNotification {
  id: string
  message_id: string
  mentioned_staff_id: string
  thread_id: string
  read_at: string | null
  created_at: string
  thread_title?: string
  sender_name?: string
  content_preview?: string
}

export async function getMentionNotifications(
  staffId: string,
  opts?: { limit?: number; unreadOnly?: boolean }, client: DbClient = defaultDbClient
): Promise<HubMentionNotification[]> {
  const limit = opts?.limit ?? 50

  let q = client
    .from('hub_mention_notifications')
    .select(
      'id, message_id, mentioned_staff_id, thread_id, read_at, created_at, thread:hub_threads!inner(title), message:hub_thread_messages!inner(content, sender_name)'
    )
    .eq('mentioned_staff_id', staffId)
    .order('created_at', { ascending: false })
    .limit(limit)

  if (opts?.unreadOnly) {
    q = q.is('read_at', null)
  }

  const { data, error } = await q
  if (error) throw new ServiceError(error.message, 'HUB_MENTIONS_LIST', error)

  return (data ?? []).map((row) => {
    const r = row as Record<string, unknown>
    const thread = (r.thread ?? {}) as { title?: string }
    const message = (r.message ?? {}) as { content?: string; sender_name?: string }
    const content = message.content ?? ''
    return {
      id: String(r.id),
      message_id: String(r.message_id),
      mentioned_staff_id: String(r.mentioned_staff_id),
      thread_id: String(r.thread_id),
      read_at: r.read_at as string | null,
      created_at: String(r.created_at),
      thread_title: thread.title ?? 'Discussion',
      sender_name: message.sender_name ?? 'Someone',
      content_preview: content.length > 80 ? `${content.slice(0, 80)}…` : content,
    }
  })
}

export async function getUnreadMentionCount(staffId: string, client: DbClient = defaultDbClient): Promise<number> {
  const { count, error } = await client
    .from('hub_mention_notifications')
    .select('*', { count: 'exact', head: true })
    .eq('mentioned_staff_id', staffId)
    .is('read_at', null)

  if (error) throw new ServiceError(error.message, 'HUB_MENTION_COUNT', error)
  return count ?? 0
}

export async function markMentionRead(mentionId: string, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client
    .from('hub_mention_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('id', mentionId)

  if (error) throw new ServiceError(error.message, 'HUB_MENTION_READ_ONE', error)
}

export async function markMentionsRead(staffId: string, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client
    .from('hub_mention_notifications')
    .update({ read_at: new Date().toISOString() })
    .eq('mentioned_staff_id', staffId)
    .is('read_at', null)

  if (error) throw new ServiceError(error.message, 'HUB_MENTION_READ', error)
}

// ─── Team Tasks ────────────────────────────────────────────────

export interface TeamTask {
  id: string
  title: string
  description: string | null
  assignee_id: string | null
  created_by: string | null
  team: string
  status: 'todo' | 'in_progress' | 'done'
  priority: 'low' | 'medium' | 'high'
  due_date: string | null
  created_at: string
  completed_at: string | null
  is_personal?: boolean
  assignee?: { id: string; name: string } | null
}

export interface CreateTeamTaskPayload {
  title: string
  description?: string | null
  assignee_id?: string | null
  team: string
  priority?: 'low' | 'medium' | 'high'
  due_date?: string | null
  is_personal?: boolean
}

export interface UpdateTeamTaskPayload {
  title?: string
  description?: string | null
  assignee_id?: string | null
  status?: 'todo' | 'in_progress' | 'done'
  priority?: 'low' | 'medium' | 'high'
  due_date?: string | null
  completed_at?: string | null
}

export async function getTeamTasks(client: DbClient = defaultDbClient): Promise<TeamTask[]> {
  const { data, error } = await client
    .from('team_tasks')
    .select('*, assignee:staff!team_tasks_assignee_id_fkey(id, name)')
    .or('is_personal.eq.false,is_personal.is.null')
    .order('created_at', { ascending: false })

  if (error) throw new ServiceError(error.message, 'TEAM_TASKS_LIST', error)

  return (data ?? []).map((row) => ({
    ...(row as TeamTask),
    assignee: (row as Record<string, unknown>).assignee ?? null,
  })) as TeamTask[]
}

export async function getMyTasks(staffId: string, client: DbClient = defaultDbClient): Promise<TeamTask[]> {
  const { data, error } = await client
    .from('team_tasks')
    .select('*, assignee:staff!team_tasks_assignee_id_fkey(id, name)')
    .or(`assignee_id.eq.${staffId},and(is_personal.eq.true,created_by.eq.${staffId})`)
    .order('created_at', { ascending: false })

  if (error) throw new ServiceError(error.message, 'MY_TASKS_LIST', error)

  return (data ?? []).map((row) => ({
    ...(row as TeamTask),
    assignee: (row as Record<string, unknown>).assignee ?? null,
  })) as TeamTask[]
}

export async function createTeamTask(
  payload: CreateTeamTaskPayload,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<TeamTask> {
  const { data, error } = await client
    .from('team_tasks')
    .insert({
      title: payload.title,
      description: payload.description ?? null,
      assignee_id: payload.assignee_id ?? null,
      team: payload.team,
      priority: payload.priority ?? 'medium',
      due_date: payload.due_date ?? null,
      created_by: actor.userId,
      is_personal: payload.is_personal ?? false,
    })
    .select('*, assignee:staff!team_tasks_assignee_id_fkey(id, name)')
    .single()

  if (error) throw new ServiceError(error.message, 'TEAM_TASK_CREATE', error)
  if (!data) throw new ServiceError('No row returned', 'TEAM_TASK_CREATE')

  await logActivity(
    withActivityActor(actor, {
      action: 'TEAM_TASK_CREATE',
      entityType: 'team_tasks',
      entityId: data.id,
      newValues: payload as unknown as Record<string, unknown>,
    }), client
  )

  return { ...data, assignee: data.assignee ?? null } as TeamTask
}

export async function updateTeamTask(
  id: string,
  payload: UpdateTeamTaskPayload,
  actor: ActivityActor, client: DbClient = defaultDbClient
): Promise<TeamTask> {
  const updateData: Record<string, unknown> = { ...payload }
  if (payload.status === 'done' && !payload.completed_at) {
    updateData.completed_at = new Date().toISOString()
  } else if (payload.status && payload.status !== 'done') {
    updateData.completed_at = null
  }

  const { data, error } = await client
    .from('team_tasks')
    .update(updateData as unknown as TablesUpdate<'team_tasks'>)
    .eq('id', id)
    .select('*, assignee:staff!team_tasks_assignee_id_fkey(id, name)')
    .single()

  if (error) throw new ServiceError(error.message, 'TEAM_TASK_UPDATE', error)
  if (!data) throw new ServiceError('No row returned', 'TEAM_TASK_UPDATE')

  await logActivity(
    withActivityActor(actor, {
      action: 'TEAM_TASK_UPDATE',
      entityType: 'team_tasks',
      entityId: id,
      newValues: updateData,
    }), client
  )

  return { ...data, assignee: data.assignee ?? null } as TeamTask
}

// ─── Staff Online Status ───────────────────────────────────────

export async function updateStaffOnlineStatus(staffId: string, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client
    .from('staff_online_status')
    .upsert(
      {
        staff_id: staffId,
        is_online: true,
        last_seen_at: new Date().toISOString(),
      },
      { onConflict: 'staff_id' }
    )
  /* Silently ignore RLS 403 — table lacks policies (non-critical) */
  if (error && error.code !== '42501') {
    throw new ServiceError(error.message, 'STAFF_ONLINE_UPDATE', error)
  }
}

export async function setStaffOffline(staffId: string, client: DbClient = defaultDbClient): Promise<void> {
  const { error } = await client
    .from('staff_online_status')
    .upsert(
      {
        staff_id: staffId,
        is_online: false,
        last_seen_at: new Date().toISOString(),
      },
      { onConflict: 'staff_id' }
    )
  /* Silently ignore RLS 403 — table lacks policies (non-critical) */
  if (error && error.code !== '42501') {
    throw new ServiceError(error.message, 'STAFF_OFFLINE_UPDATE', error)
  }
}
